import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

import {
  DEFAULT_LABEL_GROUP_COLOR,
  LabelSelectionGroup,
  LabelType,
} from '../../../../store/types';
import rootElement from '../../../../rootElement';
import { labelText } from '../../../../utils/labelText';

import './LabelGroupManagerModal.scss';

interface LabelGroupManagerModalProps {
  isOpen: boolean;
  labels: LabelType[];
  groups: LabelSelectionGroup[];
  onSave: (groups: LabelSelectionGroup[]) => void;
  onClose: () => void;
}

function nextNewGroupName(groups: LabelSelectionGroup[]) {
  const groupNames = new Set(groups.map((group) => group.name));
  let index = 1;
  while (groupNames.has(`New group ${index}`)) {
    index += 1;
  }

  return `New group ${index}`;
}

export function moveLabelBetweenGroups(
  groups: LabelSelectionGroup[],
  labelId: string,
  targetGroupName: string | null,
) {
  const strippedGroups = groups.map((group) => ({
    ...group,
    labelIds: group.labelIds.filter((id) => id !== labelId),
  }));

  if (!targetGroupName) {
    return strippedGroups;
  }

  return strippedGroups.map((group) => {
    if (group.name !== targetGroupName || group.labelIds.includes(labelId)) {
      return group;
    }

    return {
      ...group,
      labelIds: [...group.labelIds, labelId],
    };
  });
}

function DraggableLabelChip({
  label,
  onRemove,
}: {
  label: LabelType;
  onRemove?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `label:${label.id}`,
    data: { labelId: label.id },
  });

  return (
    <div
      ref={setNodeRef}
      className="label-group-manager-modal__chip"
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.55 : 1,
      }}
    >
      <button
        type="button"
        className="label-group-manager-modal__chip-drag"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${labelText(label.name)}`}
      >
        {labelText(label.name)}
      </button>
      {onRemove && (
        <button
          type="button"
          className="label-group-manager-modal__chip-remove"
          onClick={onRemove}
          aria-label={`Remove ${labelText(label.name)} from group`}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
}

function DropLane({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`label-group-manager-modal__lane ${isOver ? 'label-group-manager-modal__lane--over' : ''}`}
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function LabelGroupManagerModal({
  isOpen,
  labels,
  groups,
  onSave,
  onClose,
}: LabelGroupManagerModalProps) {
  const [localGroups, setLocalGroups] = useState(groups);

  useEffect(() => {
    if (isOpen) {
      setLocalGroups(groups);
    }
  }, [groups, isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const labelsById = useMemo(
    () => new Map(labels.map((label) => [label.id, label])),
    [labels],
  );

  const groupedLabelIds = useMemo(
    () => new Set(localGroups.flatMap((group) => group.labelIds)),
    [localGroups],
  );

  const ungroupedLabels = useMemo(
    () => labels.filter((label) => !groupedLabelIds.has(label.id)),
    [groupedLabelIds, labels],
  );

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isOpen]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const labelId = event.active.data.current?.labelId as string | undefined;
    if (!labelId || !event.over) {
      return;
    }

    if (event.over.id === 'ungrouped-lane') {
      setLocalGroups((current) => moveLabelBetweenGroups(current, labelId, null));
      return;
    }

    const overId = String(event.over.id);
    if (!overId.startsWith('group-lane:')) {
      return;
    }

    const groupIndex = Number(overId.replace('group-lane:', ''));
    const targetGroup = localGroups[groupIndex];
    if (!targetGroup) {
      return;
    }

    setLocalGroups((current) => moveLabelBetweenGroups(current, labelId, targetGroup.name));
  }, [localGroups]);

  const handleRenameGroup = (index: number, name: string) => {
    setLocalGroups((current) => current.map((group, groupIndex) => (
      groupIndex === index ? { ...group, name } : group
    )));
  };

  const handleUpdateGroupColor = (index: number, color: string) => {
    setLocalGroups((current) => current.map((group, groupIndex) => (
      groupIndex === index ? { ...group, color } : group
    )));
  };

  const handleDeleteGroup = (index: number) => {
    setLocalGroups((current) => current.filter((_, groupIndex) => groupIndex !== index));
  };

  const handleRemoveLabel = (labelId: string) => {
    setLocalGroups((current) => moveLabelBetweenGroups(current, labelId, null));
  };

  const handleAddGroupRow = () => {
    setLocalGroups((current) => current.concat({
      name: nextNewGroupName(current),
      labelIds: [],
      color: DEFAULT_LABEL_GROUP_COLOR,
    }));
  };

  const handleSave = () => {
    onSave(localGroups.map((group) => ({
      ...group,
      name: group.name.trim(),
    })));
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="label-group-manager-modal" onClick={onClose}>
      <div
        className="label-group-manager-modal__container"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Label group manager"
      >
        <header className="label-group-manager-modal__header">
          <div className="label-group-manager-modal__title">
            <h2>Label group manager</h2>
            <p>Edit group names, colours, and drag labels between groups.</p>
          </div>
          <div className="label-group-manager-modal__header-actions">
            <button
              type="button"
              className="label-group-manager-modal__save"
              onClick={handleSave}
              aria-label="Save groups"
            >
              <FontAwesomeIcon icon={faCheck} />
              <span>Save groups</span>
            </button>
            <button
              type="button"
              className="label-group-manager-modal__close"
              onClick={onClose}
              aria-label="Close group manager"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </header>

        <div className="label-group-manager-modal__toolbar">
          <button
            type="button"
            className="label-group-manager-modal__add-group"
            onClick={handleAddGroupRow}
            aria-label="Add group row"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add group</span>
          </button>
        </div>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="label-group-manager-modal__grid">
            <section className="label-group-manager-modal__ungrouped-panel">
              <div className="label-group-manager-modal__panel-title">Ungrouped labels</div>
              <DropLane id="ungrouped-lane" label="Ungrouped labels">
                {ungroupedLabels.length === 0 ? (
                  <p className="label-group-manager-modal__empty">Drag labels here to remove them from a group.</p>
                ) : (
                  <div className="label-group-manager-modal__chip-list">
                    {ungroupedLabels.map((label) => (
                      <DraggableLabelChip key={label.id} label={label} />
                    ))}
                  </div>
                )}
              </DropLane>
            </section>

            <section className="label-group-manager-modal__groups-panel">
              <div className="label-group-manager-modal__table-head">
                <span>Group</span>
                <span>Colour</span>
                <span>Members</span>
                <span>Actions</span>
              </div>
              <div className="label-group-manager-modal__table-body">
                {localGroups.map((group, index) => (
                  <div key={`group-row-${index}`} className="label-group-manager-modal__row">
                    <label className="label-group-manager-modal__field">
                      <span className="label-group-manager-modal__field-label">Name</span>
                      <input
                        aria-label={`Group name for ${group.name || `New group ${index + 1}`}`}
                        className="label-group-manager-modal__input"
                        value={group.name}
                        onChange={(event) => handleRenameGroup(index, event.target.value)}
                      />
                    </label>
                    <label className="label-group-manager-modal__field label-group-manager-modal__field--colour">
                      <span className="label-group-manager-modal__field-label">Colour</span>
                      <input
                        aria-label={`Group colour for ${group.name || `New group ${index + 1}`}`}
                        className="label-group-manager-modal__color"
                        type="color"
                        value={group.color ?? DEFAULT_LABEL_GROUP_COLOR}
                        onChange={(event) => handleUpdateGroupColor(index, event.target.value)}
                      />
                    </label>
                    <DropLane id={`group-lane:${index}`} label={`Members for ${group.name || `New group ${index + 1}`}`}>
                      {group.labelIds.length === 0 ? (
                        <p className="label-group-manager-modal__empty">Drop labels here.</p>
                      ) : (
                        <div className="label-group-manager-modal__chip-list">
                          {group.labelIds.map((labelId) => {
                            const label = labelsById.get(labelId);
                            if (!label) {
                              return null;
                            }

                            return (
                              <DraggableLabelChip
                                key={label.id}
                                label={label}
                                onRemove={() => handleRemoveLabel(label.id)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </DropLane>
                    <div className="label-group-manager-modal__row-actions">
                      <button
                        type="button"
                        className="label-group-manager-modal__delete-group"
                        onClick={() => handleDeleteGroup(index)}
                        aria-label={`Delete group ${group.name || `New group ${index + 1}`}`}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </DndContext>
      </div>
    </div>
  );

  return createPortal(modalContent, rootElement || document.body);
}

export default LabelGroupManagerModal;